import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Theme } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'
import { Button, FormControl } from '@mui/material'
import IndividualReservationSettingListItem, {
  EditableIndividualReservationSettingProps,
} from './IndividualReservationSettingListItem'
import { isEqual, omit } from 'lodash'
import { H3 } from 'ui/shorthands/Texts'
import { QuestionTooltipWithLabel } from 'ui/shorthands/QuestionTooltip'
import { IndividualReservationSettingProps } from 'types/med'
import { ReservationFrameUnitMin } from 'types/common'

// 設定値のバリデーション
const isValid = (setting: EditableIndividualReservationSettingProps) => {
  return (
    setting.start_date &&
    setting.end_date &&
    setting.reservation_frames.length > 0
  )
}

// NOTE: key用のidを付加する
let editableIndividualReservationSettingId = 1
const createEditable = (
  obj?: IndividualReservationSettingProps
): EditableIndividualReservationSettingProps => ({
  reservation_frames: obj?.reservation_frames ?? [],
  start_date: obj?.start_date ?? null,
  end_date: obj?.end_date ?? null,
  id: editableIndividualReservationSettingId++,
})

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    width: '100%',
    color: theme.palette.text.primary,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  noSetting: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2),
    fontSize: theme.typography.body1.fontSize,
  },
}))

type IndividualReservationSettingListProps = {
  /** 予約設定一覧 */
  reservationSettings: IndividualReservationSettingProps[]

  /** 内容が更新された場合に発火 */
  onChange?: (reservationSetting: IndividualReservationSettingProps[]) => void

  /** 参照のみのモードを指定 */
  readOnly?: boolean

  /** 予約枠の時間 */
  unit: ReservationFrameUnitMin
}
const IndividualReservationSettingList: React.FC<
  IndividualReservationSettingListProps
> = ({
  reservationSettings,
  onChange,
  readOnly,
  unit,
}: IndividualReservationSettingListProps) => {
  const { t } = useTranslation()

  const [settings, setSettings] = useState<
    EditableIndividualReservationSettingProps[]
  >(
    reservationSettings
      .sort(
        (a, b) =>
          Number(new Date(a.start_date)) - Number(new Date(b.start_date))
      )
      .map(createEditable)
  )

  useEffect(() => {
    setSettings((prevSettings) => {
      const invalidSettings = prevSettings.filter(
        (setting) => !isValid(setting)
      )
      return invalidSettings.concat(reservationSettings.map(createEditable))
    })
  }, [reservationSettings])

  useEffect(() => {
    const validSettings = settings
      .filter(isValid)
      .map((setting) => omit(setting, 'id')) // NOTE: 編集用の"id"フィールドを削除する
    if (!isEqual(reservationSettings, validSettings)) {
      onChange?.(validSettings as IndividualReservationSettingProps[])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings])

  const { classes } = useStyles()

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <H3>
          <QuestionTooltipWithLabel label={t('個別予約可能日')}>
            {t(
              '定期予定よりも優先して設定されます。日付を過ぎると表示されなくなります。'
            )}
          </QuestionTooltipWithLabel>
        </H3>
        {!readOnly && (
          <FormControl>
            <Button
              color="primary"
              variant="contained"
              onClick={() => {
                setSettings((prevState) => [createEditable(), ...prevState])
              }}
            >
              {t('新規作成')}
            </Button>
          </FormControl>
        )}
      </div>
      {settings.length > 0 ? (
        settings.map((setting, index) => {
          return (
            <IndividualReservationSettingListItem
              unit={unit}
              key={setting.id}
              reservationSetting={setting}
              onChange={(newSetting) => {
                setSettings((prevState) => {
                  return prevState.map((existingSetting, targetIndex) =>
                    targetIndex === index ? newSetting : existingSetting
                  )
                })
              }}
              onDelete={() => {
                setSettings((prevState) =>
                  prevState.filter((_, targetIndex) => targetIndex !== index)
                )
              }}
              readOnly={readOnly}
            />
          )
        })
      ) : (
        <div className={classes.noSetting}> {t('設定がありません')}</div>
      )}
    </div>
  )
}

export default IndividualReservationSettingList
